import { createContext, useCallback, useContext, useMemo, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { queryKeys } from '@/lib/queryKeys';
import { useCoachStreaming } from '@/hooks/useCoachStreaming';
import { parseCoachPlanRequest } from '@/lib/chatParsers';
import { toast } from 'sonner';

type StreamCtx = ReturnType<typeof useCoachStreaming>;

const CoachStreamContext = createContext<StreamCtx | null>(null);

/**
 * Hosts the coach streaming state at the app root so navigating away from
 * /coach does NOT unmount the typewriter. Persistence of the assistant
 * message also happens here so the reply lands in the DB even after the user
 * leaves the page.
 */
export function CoachStreamProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const handleStreamComplete = useCallback(
    async (content: string, meta?: { systemError?: boolean; errorType?: string | null }) => {
      if (!user?.id || !content) return;

      // No optimistic row needed: useCoachMessages maintains a stable React
      // key alias from stream key -> persisted DB UUID. When the DB row
      // arrives via the invalidate below, it inherits the same React key
      // the streaming bubble used, so the bubble stays mounted across the
      // handoff (no remount, no DOMPurify re-flash, no "paste again" blip).



      // Persist assistant message directly (avoids hook closures tied to the
      // Coach page that may have unmounted). Gateway-error fallbacks are
      // stored as message_type='system_error' so they (a) still render in the
      // chat UI but (b) get excluded from the prompt context on the next turn
      // — the model must never see its own fallback error text as something
      // it "said" and now has to be consistent with.
      try {
        const { error } = await supabase.from('chat_messages').insert({
          user_id: user.id,
          role: 'assistant',
          content,
          message_type: meta?.systemError ? 'system_error' : 'normal',
        } as never);
        if (error) {
          console.error('Failed to persist assistant message:', error);
        }
      } catch (err) {
        console.error('Failed to persist assistant message:', err);
      }

      // Await the chat-messages refetch so chatHistory contains the new
      // assistant row at the exact moment streamingMessage clears. This
      // closes the gap that caused the blue user bubble to flash + the
      // "paste again" flicker at the end of a stream.
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.chatMessages, user.id] });
      queryClient.invalidateQueries({ queryKey: queryKeys.coachMessageCount });
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });


      // If the coach asked us to (re)build the user's autonomous programme,
      // fire generation in the background.
      const planRequest = parseCoachPlanRequest(content);
      if (planRequest) {
        toast.info('Building your updated plan...');
        try {
          const { data, error } = await supabase.functions.invoke('generate-coach-program', {
            body: {
              goal_description: planRequest.goal_description,
              duration_weeks: planRequest.duration_weeks,
            },
          });
          // Edge function returns a structured 402 body when free-plan
          // program quota is exhausted. supabase-js surfaces non-2xx as
          // `error`, but the JSON body still rides on `data`.
          const payload = (data as any) ?? null;
          const rawMsg = (error as any)?.message || '';
          const isPaywall =
            payload?.code === 'program_paywall' ||
            payload?.error === 'PROGRAM_LIMIT_REACHED' ||
            /PROGRAM_LIMIT_REACHED|program_paywall|402/i.test(rawMsg);

          if (isPaywall) {
            toast.error('Upgrade to Premium to build more plans', {
              description:
                payload?.message ||
                'You\'ve reached the free plan limit. Premium unlocks unlimited programs, changes, and coach memory.',
              action: {
                label: 'Upgrade',
                onClick: () => {
                  window.location.assign('/subscription?trigger=coach_stream&source=coach_stream');
                },
              },
              duration: 8000,
            });
            return;
          }

          if (error) throw error;
          queryClient.invalidateQueries({ queryKey: queryKeys.routines });
          toast.success('Your coaching plan was updated');
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Failed to update plan';
          if (/PROGRAM_LIMIT_REACHED|program_paywall|402/i.test(msg)) {
            toast.error('Upgrade to Premium to build more plans', {
              description:
                'You\'ve reached the free plan limit. Premium unlocks unlimited programs, changes, and coach memory.',
              action: {
                label: 'Upgrade',
                onClick: () => window.location.assign('/subscription?trigger=coach_stream&source=coach_stream'),
              },
              duration: 8000,
            });
            return;
          }
          toast.error(msg);
        }
      }
    },
    [queryClient, user?.id],
  );

  const stream = useCoachStreaming({ onComplete: handleStreamComplete });

  const value = useMemo(() => stream, [stream]);

  return <CoachStreamContext.Provider value={value}>{children}</CoachStreamContext.Provider>;
}

export function useCoachStream() {
  const ctx = useContext(CoachStreamContext);
  if (!ctx) throw new Error('useCoachStream must be used inside CoachStreamProvider');
  return ctx;
}
